class WebSocketManager {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectInterval = 3000
    this.listeners = new Map()
    this.messageQueue = []
    this.url = process.env.REACT_APP_NODE_RED_WS_URL + '/sync'
  }

  // Connect to WebSocket
  connect() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      try {
        this.socket = new WebSocket(this.url)

        this.socket.onopen = () => {
          this.isConnected = true
          this.reconnectAttempts = 0

          // Send any queued messages
          this.processMessageQueue()

          resolve()
        }

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            this.handleIncomingMessage(data)
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
          }
        }

        this.socket.onclose = (event) => {
          this.isConnected = false

          if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect()
          }
        }

        this.socket.onerror = (error) => {
          console.error('WebSocket error:', error)
          this.isConnected = false
          reject(error)
        }
      } catch (error) {
        console.error('Error creating WebSocket connection:', error)
        reject(error)
      }
    })
  }

  // Schedule reconnection attempt
  scheduleReconnect() {
    this.reconnectAttempts++

    setTimeout(() => {
      this.connect().catch((error) => {
        console.error('Reconnection failed:', error)
      })
    }, this.reconnectInterval)
  }

  // Disconnect WebSocket
  disconnect() {
    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect')
      this.socket = null
      this.isConnected = false
    }
  }

  // Send a record
  sendRecord(record, type = 'sync', action = 'update') {
    const message = {
      type,
      data: record,
      action,
      timestamp: new Date().toISOString()
    }

    if (this.isConnected && this.socket.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify(message))
        return true
      } catch (error) {
        console.error('Error sending record:', error)
        this.queueMessage(message)
        return false
      }
    } else {
      this.queueMessage(message)
      return false
    }
  }

  // Queue message for when connection is restored
  queueMessage(message) {
    this.messageQueue.push(message)

    // Limit queue size to prevent memory issues
    if (this.messageQueue.length > 100) {
      this.messageQueue.shift()
    }
  }

  // Process queued messages
  processMessageQueue() {
    while (this.messageQueue.length > 0 && this.isConnected) {
      const message = this.messageQueue.shift()
      try {
        this.socket.send(JSON.stringify(message))
      } catch (error) {
        console.error('Error sending queued message:', error)
        // Put message back at the front of queue
        this.messageQueue.unshift(message)
        break
      }
    }
  }

  // Handle incoming messages
  handleIncomingMessage(data) {
    // Emit to all listeners for this message type
    const listeners = this.listeners.get(data.type) || []
    listeners.forEach((callback) => {
      try {
        callback(data)
      } catch (error) {
        console.error('Error in message listener:', error)
      }
    })

    // Also emit to 'all' listeners
    const allListeners = this.listeners.get('all') || []
    allListeners.forEach((callback) => {
      try {
        callback(data)
      } catch (error) {
        console.error('Error in all message listener:', error)
      }
    })
  }

  // Add event listener for specific message types
  addListener(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, [])
    }
    this.listeners.get(type).push(callback)

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(type)
      if (listeners) {
        const index = listeners.indexOf(callback)
        if (index > -1) {
          listeners.splice(index, 1)
        }
      }
    }
  }

  // Remove event listener
  removeListener(type, callback) {
    const listeners = this.listeners.get(type)
    if (listeners) {
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  // Remove all listeners for a type
  removeAllListeners(type) {
    if (type) {
      this.listeners.delete(type)
    } else {
      this.listeners.clear()
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      readyState: this.socket ? this.socket.readyState : WebSocket.CLOSED,
      queueLength: this.messageQueue.length,
      reconnectAttempts: this.reconnectAttempts
    }
  }

  // Send different types of records
  createAppointmentRecord(appointmentData) {
    return this.sendRecord(appointmentData, 'appointment', 'create')
  }

  updateAppointmentRecord(appointmentData) {
    return this.sendRecord(appointmentData, 'appointment', 'update')
  }

  deleteAppointmentRecord(appointmentData) {
    return this.sendRecord(appointmentData, 'appointment', 'delete')
  }
}

// Create singleton instance
const wsManager = new WebSocketManager()

// Export both the class and singleton instance
export { WebSocketManager }
export default wsManager

// Convenience functions for easy usage
export const connectWebSocket = () => wsManager.connect()
export const disconnectWebSocket = () => wsManager.disconnect()
export const sendRecord = (record, type) => wsManager.sendRecord(record, type)
export const addMessageListener = (type, callback) => wsManager.addListener(type, callback)
export const removeMessageListener = (type, callback) => wsManager.removeListener(type, callback)
export const getWebSocketStatus = () => wsManager.getConnectionStatus()

// Specific record senders
export const createAppointmentRecord = (data) => wsManager.createAppointmentRecord(data)
export const updateAppointmentRecord = (data) => wsManager.updateAppointmentRecord(data)
export const deleteAppointmentRecord = (data) => wsManager.deleteAppointmentRecord(data)
